"use client";

import * as React from "react";
import { Copy, Eraser, ExternalLink, RefreshCw, Replace } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../../../primitives/context-menu";

export interface CellMenuActions {
  onCopy?: () => void;
  onClear?: () => void;
  onRetry?: () => void;
  onOpenFileViewer?: () => void;
  onChangeSource?: () => void;
}

export interface CellMenuProps extends CellMenuActions {
  children: React.ReactNode;
  /** Disable the entire menu (e.g. locked cell). */
  disabled?: boolean;
}

/**
 * Wraps a cell with a right-click context menu exposing the standard grid
 * cell actions: copy, clear, retry, open file viewer, change source. Hidden
 * actions (no handler provided) are filtered out.
 */
export function CellMenu({
  children,
  disabled,
  onCopy,
  onClear,
  onRetry,
  onOpenFileViewer,
  onChangeSource,
}: CellMenuProps) {
  if (disabled) {
    return <>{children}</>;
  }

  const groupOne = [
    onCopy && (
      <ContextMenuItem key="copy" onSelect={() => onCopy()}>
        <Copy className="size-3.5" /> Copy value
      </ContextMenuItem>
    ),
    onOpenFileViewer && (
      <ContextMenuItem key="open" onSelect={() => onOpenFileViewer()}>
        <ExternalLink className="size-3.5" /> Open file viewer
      </ContextMenuItem>
    ),
  ].filter(Boolean);

  const groupTwo = [
    onRetry && (
      <ContextMenuItem key="retry" onSelect={() => onRetry()}>
        <RefreshCw className="size-3.5" /> Retry cell
      </ContextMenuItem>
    ),
    onChangeSource && (
      <ContextMenuItem key="source" onSelect={() => onChangeSource()}>
        <Replace className="size-3.5" /> Change source
      </ContextMenuItem>
    ),
    onClear && (
      <ContextMenuItem key="clear" variant="destructive" onSelect={() => onClear()}>
        <Eraser className="size-3.5" /> Clear value
      </ContextMenuItem>
    ),
  ].filter(Boolean);

  const hasAny = groupOne.length + groupTwo.length > 0;
  if (!hasAny) return <>{children}</>;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        {groupOne}
        {groupOne.length > 0 && groupTwo.length > 0 && <ContextMenuSeparator />}
        {groupTwo}
      </ContextMenuContent>
    </ContextMenu>
  );
}
